import { AddOrEditIntegrationDrawerFooter } from '@modules/integrationsV2/components/AddOrEditIntegrationDrawerFooter';
import { AddOrEditIntegrationDrawerHeader } from '@modules/integrationsV2/components/AddOrEditIntegrationDrawerHeader';
import { useUpdateIntegration } from '@modules/integrationsV2/hooks';
import {
  getAddIntegrationDrawerHeaderTitle,
  trackIntegrationEvent,
} from '@modules/integrationsV2/utils';
import { GetAddIntegrationFormDrawerContentProps } from '@modules/integrationsV2/utils/getAddIntegrationFormDrawerContent';
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';
import {
  IntegrationChannelType,
  IntegrationDirection,
} from 'generated/graphql';
import { useState } from 'react';
import useCreateIntegrationEdgeServer from 'v2/core/repository/edge-server/integrations';
import { IsLoading } from 'v2/core/repository/edge-server/types';
import { useGetUser } from 'v2/core/services/user';
import { isStringValidUrl } from 'v2/utils/isStringValidUrl';

const GAINSIGHT_API_REGIONS = ['eu-west-1', 'us-west-2'];

export const GainsightFormDrawerContent = ({
  integrationType,
  integrationChannel,
  onDrawerClose,
  integrationID,
}: GetAddIntegrationFormDrawerContentProps) => {
  const {
    id: userID,
    tenant: { id: tenantID },
  } = useGetUser();

  const [clientID, setClientID] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [apiRegion, setApiRegion] = useState('');
  const [communityDomain, setCommunityDomain] = useState('');
  const [isInvalidCommunityDomain, setIsInvalidCommunityDomain] =
    useState(false);

  const { status: createIntegrationStatus, createIntegration } =
    useCreateIntegrationEdgeServer({
      tenantID,
      direction: IntegrationDirection.Inbound,
      channel: IntegrationChannelType.Gainsight,
      integrationType,
      createdByUser: userID,
      inputData: {
        gainsight: {
          clientID,
          clientSecret,
          apiRegion,
          communityDomain,
        },
      },
      onCompleted: () => {
        onDrawerClose();
        trackIntegrationEvent({
          eventName: 'Integration Added',
          integrationChannelType: IntegrationChannelType.Gainsight,
        });
      },
    });

  const [updateIntegration, { status: updateIntegrationStatus }] =
    useUpdateIntegration({
      action: 'reauth',
      channelType: IntegrationChannelType.Gainsight,
      integrationType,
      onCompleted: () => {
        onDrawerClose();
      },
    });

  const isReauthorizeForm = !!integrationID;

  const onSubmit = () => {
    if (!isStringValidUrl(communityDomain)) {
      setIsInvalidCommunityDomain(true);
      return;
    }
    if (isReauthorizeForm) {
      updateIntegration({
        integrationID,
        isActive: true,
        updateAuthData: {
          gainsight: {
            clientID,
            clientSecret,
            apiRegion,
            communityDomain,
          },
        },
      });
    } else {
      createIntegration();
    }
  };

  const isDisabled = !clientID.trim() || !clientSecret.trim();

  const isLoading =
    IsLoading(createIntegrationStatus) || IsLoading(updateIntegrationStatus);

  return (
    <Stack height="100%" justifyContent="space-between">
      <Box>
        <AddOrEditIntegrationDrawerHeader
          integrationChannel={integrationChannel}
          integrationType={integrationType}
          title={getAddIntegrationDrawerHeaderTitle(
            integrationChannel,
            integrationType,
          )}
          isReauthorizeForm={isReauthorizeForm}
        />
        <Stack p={3} spacing={4}>
          <TextField
            required
            fullWidth
            label="Client ID"
            placeholder="Enter Client ID"
            variant="outlined"
            onChange={(e) => setClientID(e.target.value)}
            disabled={isLoading}
          />
          <TextField
            required
            fullWidth
            label="Client Secret"
            placeholder="Enter Client Secret"
            variant="outlined"
            onChange={(e) => setClientSecret(e.target.value)}
            disabled={isLoading}
          />
          <FormControl required>
            <InputLabel id="api-region-select-label">
              Gainsight API Region
            </InputLabel>
            <Select
              labelId="api-region-select-label"
              label="Gainsight API Region"
              onChange={(event) => setApiRegion(event.target.value)}
              value={apiRegion}
            >
              {GAINSIGHT_API_REGIONS.map((region) => {
                return (
                  <MenuItem key={region} value={region}>
                    {region}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
          <TextField
            required
            fullWidth
            label="Community URL"
            placeholder="Enter Community URL"
            variant="outlined"
            onChange={(e) => {
              if (isInvalidCommunityDomain) {
                setIsInvalidCommunityDomain(false);
              }
              setCommunityDomain(e.target.value);
            }}
            disabled={isLoading}
            error={isInvalidCommunityDomain}
            helperText={
              isInvalidCommunityDomain ? 'Please input a valid URL' : null
            }
          />
        </Stack>
      </Box>
      <AddOrEditIntegrationDrawerFooter
        onCancel={onDrawerClose}
        onSubmit={onSubmit}
        buttonText={isReauthorizeForm ? 'Reauthorize' : 'Integrate'}
        isDisabled={isDisabled}
        isLoading={isLoading}
      />
    </Stack>
  );
};
